import { type ComponentType, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronLeft, ChevronRight, type LucideProps } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

// Cortex Sidebar (DESIGN_LANGUAGE_SPEC §20). Collapsible (w-72 / w-20) with a 300ms width
// transition, gradient-accented active nav items (active indicator bar + icon container),
// label+description, a logo header, and a dark-only ambient glow. Nav uses react-router
// NavLink so the active item is derived from the route.

export interface NavItemConfig {
  to: string;
  label: string;
  description?: string | undefined;
  icon: ComponentType<LucideProps>;
  /** Pass true for index routes so the active match is exact. */
  end?: boolean | undefined;
}

export interface SidebarProps {
  items: NavItemConfig[];
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Branding shown in the header (e.g. logo + product name). */
  brand?: ReactNode | undefined;
  /** Optional footer content (e.g. a user menu). */
  footer?: ReactNode | undefined;
}

export function Sidebar({
  items,
  collapsed,
  onToggleCollapsed,
  brand,
  footer,
}: SidebarProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <aside
      data-testid="sidebar"
      data-collapsed={collapsed}
      className={`relative z-20 flex flex-col border-r transition-all duration-300 ease-out ${
        collapsed ? 'w-20' : 'w-72'
      } ${
        isDark
          ? 'border-white/[0.06] bg-gradient-to-b from-[#12121a] to-[#0d0d14]'
          : 'border-slate-200 bg-white'
      }`}
    >
      {/* Ambient glow — dark mode only */}
      {isDark && (
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-500/[0.02] via-transparent to-purple-500/[0.02]"
          aria-hidden="true"
        />
      )}

      {/* Logo header */}
      <div
        className={`flex h-16 items-center gap-3 border-b px-4 ${
          isDark ? 'border-white/[0.06]' : 'border-slate-200'
        } ${collapsed ? 'justify-center' : 'justify-between'}`}
      >
        {!collapsed && <div className="min-w-0 flex-1">{brand}</div>}
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${
            isDark
              ? 'text-white/40 hover:bg-white/[0.06] hover:text-white'
              : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
          }`}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* Nav items */}
      <nav className="flex-1 space-y-1 overflow-y-auto p-3" aria-label="Primary">
        {items.map(({ to, label, description, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end ?? false}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
              `group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${
                isActive
                  ? isDark
                    ? 'bg-gradient-to-r from-indigo-500/20 to-purple-500/10 text-white'
                    : 'bg-gradient-to-r from-indigo-500/10 to-purple-500/5 text-indigo-600'
                  : isDark
                    ? 'text-white/50 hover:bg-white/[0.04] hover:text-white'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
              }`
            }
          >
            {({ isActive }) => (
              <>
                {/* Active indicator bar */}
                {isActive && (
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full bg-gradient-to-b from-indigo-400 to-purple-500"
                  />
                )}

                {/* Icon container */}
                <span
                  className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25'
                      : isDark
                        ? 'bg-white/[0.04] text-white/50 group-hover:bg-white/[0.08] group-hover:text-white'
                        : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200 group-hover:text-slate-600'
                  }`}
                >
                  <Icon size={18} aria-hidden="true" />
                </span>

                {/* Label + description */}
                {!collapsed && (
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">{label}</span>
                    {description && (
                      <span
                        className={`block truncate text-[11px] ${
                          isDark ? 'text-white/30' : 'text-slate-400'
                        }`}
                      >
                        {description}
                      </span>
                    )}
                  </span>
                )}

                {isActive && !collapsed && (
                  <ChevronRight
                    size={16}
                    aria-hidden="true"
                    className={isDark ? 'text-white/30' : 'text-slate-400'}
                  />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      {footer && (
        <div
          className={`border-t p-3 ${
            isDark ? 'border-white/[0.06]' : 'border-slate-200'
          }`}
        >
          {footer}
        </div>
      )}
    </aside>
  );
}
